import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { AssistantConversation } from "@/components/Assistant/AssistantConversation";

/**
 * Full-page assistant at /asistente.
 *
 * Renders the same <AssistantConversation> as the slide-over, so the thread is
 * identical either way — only the chrome and the reading width differ.
 *
 * `MarketplaceShell` does not render the floating launcher on this route.
 * That is load-bearing, not tidiness: two conversations mounted on one scope
 * would each run a hydration fetch and each arm the automatic approval resume.
 */
export default function AssistantFullscreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, forceRender] = useState(0);
  const startNewRef = useRef<(() => void) | null>(null);

  return (
    // Fills the shell's main area. min-h-0 lets the message list scroll instead
    // of pushing the composer off the bottom.
    <div className="flex flex-col h-[calc(100vh-3.5rem)] min-h-0 bg-white">
      <div className="border-b border-neutral-200 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-neutral-900">
              {t("assistant.fullscreen.title")}
            </h1>
            <p className="text-xs text-neutral-400">{t("assistant.fullscreen.subtitle")}</p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => startNewRef.current?.()}
              title={t("assistant.newConversation")}
              className="w-8 h-8 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate(-1)}
              title={t("assistant.fullscreen.exit")}
              aria-label={t("assistant.fullscreen.exit")}
              className="w-8 h-8 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* The conversation renders its own scroll area and composer; the wrapper
          just constrains reading width on wide screens. */}
      <div className="flex-1 flex flex-col min-h-0 w-full max-w-3xl mx-auto">
        <AssistantConversation
          messagesClassName="px-6 py-6"
          inputClassName="px-6 py-4"
          onReady={({ startNewConversation }) => {
            startNewRef.current = startNewConversation;
            forceRender((n) => (n === 0 ? 1 : n));
          }}
        />
      </div>
    </div>
  );
}
